/**
 * 配置服務
 * 
 * 管理應用程式的配置和設置
 */

export interface AppConfig {
  // 自動登出時間 (分鐘)
  sessionTimeout: number;
  // 其他配置項目可以在此添加
}

// 默認配置
const DEFAULT_CONFIG: AppConfig = {
  sessionTimeout: 480, // 默認 30 分鐘
};

/**
 * 配置服務類
 */
class ConfigService {
  private readonly CONFIG_KEY = 'app_config';
  private config: AppConfig;

  constructor() {
    this.config = this.loadConfig();
  }

  /**
   * 從本地存儲載入配置
   */
  private loadConfig(): AppConfig {
    try {
      const storedConfig = localStorage.getItem(this.CONFIG_KEY);
      if (storedConfig) {
        return { ...DEFAULT_CONFIG, ...JSON.parse(storedConfig) };
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    }
    return { ...DEFAULT_CONFIG };
  }

  /**
   * 保存配置到本地存儲
   */
  private saveConfig(): void {
    try {
      localStorage.setItem(this.CONFIG_KEY, JSON.stringify(this.config));
    } catch (error) {
      console.error('Failed to save config:', error);
    }
  }

  /**
   * 獲取完整配置
   */
  getConfig(): AppConfig {
    return { ...this.config };
  }

  /**
   * 獲取自動登出時間 (毫秒)
   */
  getSessionTimeoutMs(): number {
    return this.config.sessionTimeout * 60 * 1000;
  }

  /**
   * 設置自動登出時間 (分鐘)
   */
  setSessionTimeout(minutes: number): void {
    if (minutes < 1) minutes = 1; // 最小 1 分鐘
    if (minutes > 1440) minutes = 1440; // 最大 24 小時

    this.config.sessionTimeout = minutes;
    this.saveConfig();
  }

  /**
   * 更新配置
   */
  updateConfig(newConfig: Partial<AppConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.saveConfig();
  }

  /**
   * 重置配置為默認值
   */
  resetConfig(): void {
    this.config = { ...DEFAULT_CONFIG };
    this.saveConfig();
  }
}

// 導出單例實例
export const configService = new ConfigService();
